import { createSlice, PayloadAction } from "@reduxjs/toolkit";

export interface Patient {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  dateOfBirth: string;
  gender: "male" | "female" | "other";
  address: {
    street: string;
    city: string;
    state: string;
    zipCode: string;
    country: string;
  };
  medicalHistory: string[];
  allergies: string[];
  emergencyContact: {
    name: string;
    phone: string;
    relationship: string;
  };
  insurance: {
    provider: string;
    policyNumber: string;
    groupNumber: string;
  };
  notes: string;
  status: "active" | "inactive" | "archived";
  createdAt: string;
  updatedAt: string;
}

export interface PatientsState {
  patients: Patient[];
  selectedPatient: Patient | null;
  isLoading: boolean;
  error: string | null;
  searchQuery: string;
  filters: {
    status: string[];
    gender: string[];
    ageRange: [number, number];
  };
  pagination: {
    page: number;
    limit: number;
    total: number;
  };
}

const initialState: PatientsState = {
  patients: [],
  selectedPatient: null,
  isLoading: false,
  error: null,
  searchQuery: "",
  filters: {
    status: [],
    gender: [],
    ageRange: [0, 100],
  },
  pagination: {
    page: 1,
    limit: 10,
    total: 0,
  },
};

const patientsSlice = createSlice({
  name: "patients",
  initialState,
  reducers: {
    setPatientsLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    setPatientsError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    setPatients: (state, action: PayloadAction<Patient[]>) => {
      state.patients = action.payload;
    },
    addPatient: (state, action: PayloadAction<Patient>) => {
      state.patients.unshift(action.payload);
      state.pagination.total += 1;
    },
    updatePatient: (state, action: PayloadAction<Patient>) => {
      const index = state.patients.findIndex((p) => p.id === action.payload.id);
      if (index !== -1) {
        state.patients[index] = action.payload;
      }
    },
    deletePatient: (state, action: PayloadAction<string>) => {
      state.patients = state.patients.filter((p) => p.id !== action.payload);
      state.pagination.total -= 1;
    },
    setSelectedPatient: (state, action: PayloadAction<Patient | null>) => {
      state.selectedPatient = action.payload;
    },
    setPatientSearchQuery: (state, action: PayloadAction<string>) => {
      state.searchQuery = action.payload;
    },
    setPatientFilters: (
      state,
      action: PayloadAction<Partial<PatientsState["filters"]>>
    ) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    setPatientPagination: (
      state,
      action: PayloadAction<Partial<PatientsState["pagination"]>>
    ) => {
      state.pagination = { ...state.pagination, ...action.payload };
    },
    clearPatients: (state) => {
      state.patients = [];
      state.selectedPatient = null;
      state.searchQuery = "";
      state.filters = {
        status: [],
        gender: [],
        ageRange: [0, 100],
      };
      state.pagination = {
        page: 1,
        limit: 10,
        total: 0,
      };
    },
  },
});

export const {
  setPatientsLoading,
  setPatientsError,
  setPatients,
  addPatient,
  updatePatient,
  deletePatient,
  setSelectedPatient,
  setPatientSearchQuery,
  setPatientFilters,
  setPatientPagination,
  clearPatients,
} = patientsSlice.actions;

export default patientsSlice.reducer;
